import { FC } from 'react'
import { Building2, MapPin, User, ChevronRight } from 'lucide-react'
import { Motion } from '@/components/ui/motion'
import { InstallerProject } from '@/hooks/useProjectsInstaller'
import { useTranslation } from '@/hooks/useTranslation'

interface InstallerProjectsListProps {
  projects: InstallerProject[]
  onSelect: (project: InstallerProject) => void
  emptyMessage?: string
}

const getEndCustomer = (project: InstallerProject) => {
  const p = project.project
  if (!p) return ''
  if (p.businessName) return p.businessName
  const fullName = [p.customerFirstName, p.customerLastName].filter(Boolean).join(' ')
  return fullName
}

const InstallerProjectsList: FC<InstallerProjectsListProps> = ({ projects, onSelect, emptyMessage }) => {
  const { t } = useTranslation()

  if (projects.length === 0) {
    return (
      <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
        <div className='flex items-center justify-center p-10'>
          <div className='text-center'>
            <Building2 className='mx-auto h-10 w-10 mb-3 text-[hsl(var(--muted-foreground))]' />
            <p className='text-sm text-[hsl(var(--muted-foreground))]'>
              {emptyMessage || t('MY_PROJECTS_EMPTY')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
      {projects.map((project, index) => (
        <Motion key={project.id} variant='slideUp' duration={300} delay={index * 60}>
          <button
            type='button'
            onClick={() => onSelect(project)}
            className='w-full text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] cursor-pointer card-hover'
          >
            <div className='p-5 flex items-start gap-3'>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 mb-2'>
                  <Building2 className='h-5 w-5 flex-shrink-0 text-[var(--brand-red)]' />
                  <h4 className='text-lg font-semibold truncate'>
                    {project.project?.projectNumber || t('INSTALLER_PROJECT_DEFAULT')}
                  </h4>
                </div>

                {getEndCustomer(project) && (
                  <p className='text-sm flex items-center gap-1 mb-1 text-[hsl(var(--muted-foreground))]'>
                    <User className='h-3 w-3 flex-shrink-0' />
                    <span className='truncate'>{getEndCustomer(project)}</span>
                  </p>
                )}

                {(project.project?.address || project.project?.city) && (
                  <p className='text-sm flex items-center gap-1 text-[hsl(var(--muted-foreground))]'>
                    <MapPin className='h-3 w-3 flex-shrink-0' />
                    <span className='truncate'>
                      {[project.project.address, project.project.city, project.project.province]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </p>
                )}
              </div>

              <ChevronRight className='h-5 w-5 flex-shrink-0 text-[hsl(var(--muted-foreground))]' />
            </div>
          </button>
        </Motion>
      ))}
    </div>
  )
}

export default InstallerProjectsList
